// src/utils/annotationValidator.js

/**
 * Custom error classes for annotation validation
 */
export class AnnotationValidationError extends Error {
  constructor(message, code, details = {}) {
    super(message);
    this.name = 'AnnotationValidationError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Validation result structure
 */
export class ValidationResult {
  constructor() {
    this.isValid = true;
    this.errors = [];
    this.warnings = [];
    this.validAnnotations = {};
    this.invalidAnnotations = {};
    this.stats = {
      totalFrames: 0,
      totalAnnotations: 0,
      validAnnotations: 0,
      invalidAnnotations: 0,
      correctedAnnotations: 0
    };
  }

  addError(frameIndex, annotationIndex, error, annotation = null) {
    // Don't automatically set isValid to false - we'll determine this later
    this.errors.push({
      frameIndex,
      annotationIndex,
      error,
      annotation
    });
    this.stats.invalidAnnotations++;
  }

  addWarning(frameIndex, annotationIndex, warning, annotation = null) {
    this.warnings.push({
      frameIndex,
      annotationIndex,
      warning,
      annotation
    });
  }

  addValidAnnotation(frameIndex, annotation, wasCorrected = false) {
    if (!this.validAnnotations[frameIndex]) {
      this.validAnnotations[frameIndex] = [];
    }
    this.validAnnotations[frameIndex].push(annotation);
    this.stats.validAnnotations++;
    if (wasCorrected) {
      this.stats.correctedAnnotations++;
    }
  }

  addInvalidAnnotation(frameIndex, annotation, error) {
    if (!this.invalidAnnotations[frameIndex]) {
      this.invalidAnnotations[frameIndex] = [];
    }
    this.invalidAnnotations[frameIndex].push({ annotation, error });
  }
}

/**
 * Comprehensive annotation validator
 */
export class AnnotationValidator {
  constructor(options = {}) {
    this.options = {
      strictMode: false, // If true, fails on any invalid annotation
      autoCorrect: true, // If true, attempts to correct invalid coordinates
      requireLabel: true, // If true, requires non-empty labels
      maxLabelLength: 100, // Maximum label length
      allowEmptyFrames: true, // If true, allows frames with no annotations
      ...options
    };
  }

  /**
   * Validates all annotations before export
   * @param {Object} annotationsByFrame - Annotations grouped by frame
   * @param {Object} imageMetadata - Image dimensions and metadata
   * @returns {ValidationResult} Validation results with valid/invalid annotations
   */
  validateAnnotations(annotationsByFrame, imageMetadata) {
    const result = new ValidationResult();
    
    try {
      // Validate input parameters
      this._validateInputs(annotationsByFrame, imageMetadata);
      
      const { cols, rows } = imageMetadata;
      
      // Process each frame
      for (const [frameKey, annotations] of Object.entries(annotationsByFrame || {})) {
        const frameIndex = this._validateFrameIndex(frameKey);
        if (frameIndex === null) continue;
        
        result.stats.totalFrames++;
        
        if (!Array.isArray(annotations)) {
          result.addError(frameIndex, null, 'Frame annotations must be an array', annotations);
          continue;
        }
        
        if (annotations.length === 0 && !this.options.allowEmptyFrames) {
          result.addError(frameIndex, null, 'Frame cannot have empty annotations when allowEmptyFrames is false');
          continue;
        }
        
        // Process each annotation in the frame
        annotations.forEach((annotation, annotationIndex) => {
          result.stats.totalAnnotations++;
          
          try {
            const validatedAnnotation = this._validateSingleAnnotation(
              annotation, 
              cols, 
              rows, 
              frameIndex, 
              annotationIndex
            );
            
            if (validatedAnnotation.wasCorrected) {
              result.addWarning(
                frameIndex, 
                annotationIndex, 
                `Annotation coordinates were corrected: ${validatedAnnotation.correctionReason}`,
                annotation
              );
            }
            
            result.addValidAnnotation(frameIndex, validatedAnnotation.annotation, validatedAnnotation.wasCorrected);
            
          } catch (error) {
            result.addError(frameIndex, annotationIndex, error.message, annotation);
            result.addInvalidAnnotation(frameIndex, annotation, error.message);
            
            if (this.options.strictMode) {
              throw new AnnotationValidationError(
                `Strict mode: Invalid annotation in frame ${frameIndex}, annotation ${annotationIndex}: ${error.message}`,
                'STRICT_MODE_VIOLATION',
                { frameIndex, annotationIndex, annotation, originalError: error }
              );
            }
          }
        });
      }
      
      // Final validation checks
      this._performFinalValidation(result);
      
      return result;
      
    } catch (error) {
      if (error instanceof AnnotationValidationError) {
        throw error;
      }
      
      throw new AnnotationValidationError(
        `Annotation validation failed: ${error.message}`,
        'VALIDATION_FAILED',
        { originalError: error }
      );
    }
  }

  /**
   * Validates input parameters
   */
  _validateInputs(annotationsByFrame, imageMetadata) {
    if (!imageMetadata || typeof imageMetadata !== 'object') {
      throw new AnnotationValidationError(
        'Image metadata is required for annotation validation',
        'MISSING_IMAGE_METADATA'
      );
    }
    
    const { cols, rows } = imageMetadata;
    
    if (!cols || !rows || cols <= 0 || rows <= 0) {
      throw new AnnotationValidationError(
        `Invalid image dimensions: ${cols}x${rows}`,
        'INVALID_IMAGE_DIMENSIONS',
        { cols, rows }
      );
    }
    
    if (cols > 10000 || rows > 10000) {
      throw new AnnotationValidationError(
        `Image dimensions too large: ${cols}x${rows}`,
        'IMAGE_TOO_LARGE',
        { cols, rows }
      );
    }
  }

  /**
   * Validates and normalizes frame index
   */
  _validateFrameIndex(frameKey) {
    const frameIndex = parseInt(frameKey, 10);
    if (Number.isNaN(frameIndex) || frameIndex < 0) {
      console.warn(`Invalid frame index: ${frameKey}`);
      return null;
    }
    return frameIndex;
  }

  /**
   * Validates a single annotation with auto-correction if enabled
   */
  _validateSingleAnnotation(annotation, cols, rows, frameIndex, annotationIndex) {
    if (!annotation || typeof annotation !== 'object') {
      throw new Error('Annotation must be a non-null object');
    }

    let wasCorrected = false;
    let correctionReason = '';
    const correctedAnnotation = { ...annotation };

    // Check if this is a geometric annotation
    if (annotation.type && ['line', 'rectangle', 'sketch'].includes(annotation.type)) {
      return this._validateGeometricAnnotation(correctedAnnotation, cols, rows);
    }

    // This is a tag annotation - use the original validation logic
    // Validate and process coordinates - this will add all coordinate fields
    const coordResult = this._validateCoordinates(correctedAnnotation, cols, rows);
    if (coordResult.wasCorrected) {
      wasCorrected = true;
      correctionReason = coordResult.reason;
    }
    // Always apply coordinate results to ensure all coordinate fields are present
    Object.assign(correctedAnnotation, coordResult.coordinates);

    // Validate label
    const labelResult = this._validateLabel(correctedAnnotation.label);
    if (labelResult.wasCorrected) {
      wasCorrected = true;
      correctionReason += (correctionReason ? '; ' : '') + labelResult.reason;
      correctedAnnotation.label = labelResult.label;
    }

    // Validate required fields are now present
    this._validateRequiredFields(correctedAnnotation);

    return {
      annotation: correctedAnnotation,
      wasCorrected,
      correctionReason
    };
  }

  /**
   * Validates geometric annotations (line, rectangle, sketch)
   */
  _validateGeometricAnnotation(annotation, cols, rows) {
    let wasCorrected = false;
    let correctionReason = '';
    const correctedAnnotation = { ...annotation };

    // Validate type
    if (!['line', 'rectangle', 'sketch'].includes(annotation.type)) {
      throw new Error(`Invalid geometric annotation type: ${annotation.type}`);
    }

    // Validate color
    if (!annotation.color || typeof annotation.color !== 'string') {
      if (this.options.autoCorrect) {
        correctedAnnotation.color = '#22D3EE';
        wasCorrected = true;
        correctionReason = 'missing color set to default';
      } else {
        throw new Error('Geometric annotation must have a color');
      }
    }

    // Validate ID
    if (!annotation.id) {
      correctedAnnotation.id = Date.now() + Math.random();
      wasCorrected = true;
      correctionReason += (correctionReason ? '; ' : '') + 'missing ID generated';
    }

    // Validate coordinates based on type
    if (annotation.type === 'line' || annotation.type === 'rectangle') {
      const coordResult = this._validateGeometricCoordinates(
        annotation, 
        ['startX', 'startY', 'endX', 'endY']
      );
      if (coordResult.wasCorrected) {
        wasCorrected = true;
        correctionReason += (correctionReason ? '; ' : '') + coordResult.reason;
      }
      Object.assign(correctedAnnotation, coordResult.coordinates);
    } else if (annotation.type === 'sketch') {
      const pointsResult = this._validateSketchPoints(annotation.points);
      if (pointsResult.wasCorrected) {
        wasCorrected = true;
        correctionReason += (correctionReason ? '; ' : '') + pointsResult.reason;
      }
      correctedAnnotation.points = pointsResult.points;
    }

    return {
      annotation: correctedAnnotation,
      wasCorrected,
      correctionReason
    };
  }

  /**
   * Validates geometric coordinates (normalized 0-1 range)
   */
  _validateGeometricCoordinates(annotation, coordFields) {
    let wasCorrected = false;
    let reason = '';
    const coordinates = {};

    for (const field of coordFields) {
      let value = annotation[field];
      
      if (typeof value !== 'number') {
        throw new Error(`Geometric annotation field '${field}' must be a number`);
      }
      
      if (isNaN(value)) {
        throw new Error(`Geometric annotation field '${field}' cannot be NaN`);
      }
      
      // Auto-correct out-of-bounds coordinates
      if (this.options.autoCorrect && (value < 0 || value > 1)) {
        const originalValue = value;
        value = Math.max(0, Math.min(1, value));
        wasCorrected = true;
        reason += (reason ? '; ' : '') + `${field} clamped from ${originalValue.toFixed(3)} to ${value.toFixed(3)}`;
      } else if (value < 0 || value > 1) {
        throw new Error(`Geometric annotation field '${field}' out of range [0,1]: ${value}`);
      }
      
      coordinates[field] = value;
    }

    return { wasCorrected, reason, coordinates };
  }

  /**
   * Validates sketch points
   */
  _validateSketchPoints(points) {
    let wasCorrected = false;
    let reason = '';
    
    if (!Array.isArray(points)) {
      throw new Error('Sketch annotation points must be an array');
    }
    
    if (points.length < 2) {
      throw new Error('Sketch annotation must have at least 2 points');
    }
    
    const validatedPoints = [];
    
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      
      if (!point || typeof point !== 'object') {
        throw new Error(`Sketch point ${i} must be an object`);
      }
      
      let { x, y } = point;
      
      if (typeof x !== 'number' || typeof y !== 'number') {
        throw new Error(`Sketch point ${i} coordinates must be numbers`);
      }
      
      if (isNaN(x) || isNaN(y)) {
        throw new Error(`Sketch point ${i} coordinates cannot be NaN`);
      }
      
      // Auto-correct out-of-bounds coordinates
      if (this.options.autoCorrect && (x < 0 || x > 1 || y < 0 || y > 1)) {
        const originalX = x;
        const originalY = y;
        x = Math.max(0, Math.min(1, x));
        y = Math.max(0, Math.min(1, y));
        wasCorrected = true;
        reason += (reason ? '; ' : '') + `point ${i} clamped from (${originalX.toFixed(3)}, ${originalY.toFixed(3)}) to (${x.toFixed(3)}, ${y.toFixed(3)})`;
      } else if (x < 0 || x > 1 || y < 0 || y > 1) {
        throw new Error(`Sketch point ${i} coordinates out of range [0,1]: (${x}, ${y})`);
      }
      
      validatedPoints.push({ x, y });
    }
    
    return { wasCorrected, reason, points: validatedPoints };
  }

  /**
   * Validates and corrects coordinates
   */
  _validateCoordinates(annotation, cols, rows) {
    let wasCorrected = false;
    let reason = '';
    const coordinates = {};

    // Check if we have normalized coordinates (ix/iy properties)
    const hasNormalizedCoords = (
      typeof annotation.ix === "number" && 
      typeof annotation.iy === "number"
    );
    
    const hasPixelCoords = (
      typeof annotation.x === "number" && 
      typeof annotation.y === "number"
    );

    if (!hasNormalizedCoords && !hasPixelCoords) {
      throw new Error('Annotation must have either normalized coordinates (ix, iy) or pixel coordinates (x, y)');
    }

    let px, py, normalizedX, normalizedY;
    
    if (hasNormalizedCoords) {
      // Validate normalized coordinates
      let ix = annotation.ix;
      let iy = annotation.iy;
      
      if (typeof ix !== 'number' || typeof iy !== 'number') {
        throw new Error('Normalized coordinates (ix, iy) must be numbers');
      }
      
      if (isNaN(ix) || isNaN(iy)) {
        throw new Error('Normalized coordinates (ix, iy) cannot be NaN');
      }
      
      // Auto-correct out-of-bounds normalized coordinates
      if (this.options.autoCorrect && (ix < 0 || ix > 1 || iy < 0 || iy > 1)) {
        const originalIx = ix;
        const originalIy = iy;
        ix = Math.max(0, Math.min(1, ix));
        iy = Math.max(0, Math.min(1, iy));
        wasCorrected = true;
        reason = `normalized coordinates clamped from (${originalIx.toFixed(3)}, ${originalIy.toFixed(3)}) to (${ix.toFixed(3)}, ${iy.toFixed(3)})`;
      } else if (ix < 0 || ix > 1 || iy < 0 || iy > 1) {
        throw new Error(`Normalized coordinates out of range [0,1]: (${ix}, ${iy})`);
      }
      
      // Convert to pixel coordinates
      px = Math.round(ix * (cols - 1));
      py = Math.round(iy * (rows - 1));
      normalizedX = ix;
      normalizedY = iy;
      
    } else {
      // Working with pixel coordinates
      px = annotation.x;
      py = annotation.y;
      
      if (typeof px !== 'number' || typeof py !== 'number') {
        throw new Error('Pixel coordinates (x, y) must be numbers');
      }
      
      if (isNaN(px) || isNaN(py)) {
        throw new Error('Pixel coordinates (x, y) cannot be NaN');
      }
      
      // Auto-correct out-of-bounds pixel coordinates
      if (this.options.autoCorrect && (px < 0 || px >= cols || py < 0 || py >= rows)) {
        const originalPx = px;
        const originalPy = py;
        px = Math.max(0, Math.min(cols - 1, Math.round(px)));
        py = Math.max(0, Math.min(rows - 1, Math.round(py)));
        wasCorrected = true;
        reason = `pixel coordinates clamped from (${originalPx}, ${originalPy}) to (${px}, ${py})`;
      } else if (px < 0 || px >= cols || py < 0 || py >= rows) {
        throw new Error(`Pixel coordinates out of bounds [0,${cols-1}]x[0,${rows-1}]: (${px}, ${py})`);
      }
      
      // Calculate normalized coordinates
      normalizedX = px / (cols - 1);
      normalizedY = py / (rows - 1);
    }

    // Ensure coordinates are integers for pixel values
    px = Math.round(px);
    py = Math.round(py);

    coordinates.x = px;
    coordinates.y = py;
    coordinates.ix = normalizedX;
    coordinates.iy = normalizedY;

    return { wasCorrected, reason, coordinates };
  }

  /**
   * Validates and corrects label
   */
  _validateLabel(label) {
    let wasCorrected = false;
    let reason = '';
    let correctedLabel = label;

    if (this.options.requireLabel) {
      if (label === null || label === undefined) {
        if (this.options.autoCorrect) {
          correctedLabel = 'Unlabeled';
          wasCorrected = true;
          reason = 'missing label replaced with default';
        } else {
          throw new Error('Annotation label is required');
        }
      }
    }

    // Convert to string and trim
    correctedLabel = String(correctedLabel || '').trim();

    if (this.options.requireLabel && !correctedLabel) {
      if (this.options.autoCorrect) {
        correctedLabel = 'Unlabeled';
        wasCorrected = true;
        reason = 'empty label replaced with default';
      } else {
        throw new Error('Annotation label cannot be empty');
      }
    }

    // Check label length
    if (correctedLabel.length > this.options.maxLabelLength) {
      if (this.options.autoCorrect) {
        correctedLabel = correctedLabel.substring(0, this.options.maxLabelLength);
        wasCorrected = true;
        reason = `label truncated to ${this.options.maxLabelLength} characters`;
      } else {
        throw new Error(`Label too long (${correctedLabel.length} > ${this.options.maxLabelLength})`);
      }
    }

    return { wasCorrected, reason, label: correctedLabel };
  }

  /**
   * Validates required fields are present
   */
  _validateRequiredFields(annotation) {
    // For geometric annotations, different fields are required
    if (annotation.type && ['line', 'rectangle', 'sketch'].includes(annotation.type)) {
      const requiredFields = ['type', 'color', 'id'];
      
      for (const field of requiredFields) {
        if (!(field in annotation)) {
          throw new Error(`Required field '${field}' is missing from geometric annotation`);
        }
      }
      
      // Type-specific field validation
      if (annotation.type === 'line' || annotation.type === 'rectangle') {
        const coordFields = ['startX', 'startY', 'endX', 'endY'];
        for (const field of coordFields) {
          if (!(field in annotation)) {
            throw new Error(`Required field '${field}' is missing from ${annotation.type} annotation`);
          }
        }
      } else if (annotation.type === 'sketch') {
        if (!('points' in annotation)) {
          throw new Error(`Required field 'points' is missing from sketch annotation`);
        }
      }
    } else {
      // For tag annotations, use original validation
      const requiredFields = ['x', 'y', 'ix', 'iy', 'label'];
      
      for (const field of requiredFields) {
        if (!(field in annotation)) {
          throw new Error(`Required field '${field}' is missing from annotation`);
        }
      }
    }
  }

  /**
   * Performs final validation checks on the complete result
   */
  _performFinalValidation(result) {
    // Determine if the result is valid based on whether we have any valid annotations
    result.isValid = result.stats.validAnnotations > 0 || result.stats.totalAnnotations === 0;
    
    // Check if we have any valid annotations when we have total annotations
    if (result.stats.validAnnotations === 0 && result.stats.totalAnnotations > 0) {
      throw new AnnotationValidationError(
        'No valid annotations found for export',
        'NO_VALID_ANNOTATIONS',
        { stats: result.stats }
      );
    }

    // Log validation summary
    console.log('Annotation validation summary:', {
      totalFrames: result.stats.totalFrames,
      totalAnnotations: result.stats.totalAnnotations,
      validAnnotations: result.stats.validAnnotations,
      invalidAnnotations: result.stats.invalidAnnotations,
      correctedAnnotations: result.stats.correctedAnnotations,
      errors: result.errors.length,
      warnings: result.warnings.length
    });

    // Log warnings if any
    if (result.warnings.length > 0) {
      console.warn('Annotation validation warnings:', result.warnings);
    }

    // Log errors if any
    if (result.errors.length > 0) {
      console.error('Annotation validation errors:', result.errors);
    }
  }
}

/**
 * Convenience function for quick validation
 */
export function validateAnnotationsForExport(annotationsByFrame, imageMetadata, options = {}) {
  const validator = new AnnotationValidator(options);
  return validator.validateAnnotations(annotationsByFrame, imageMetadata);
}